import React, { FC, ReactNode } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AffinityBands,
  AFFINITY_GRADES,
  GRADE_META,
  DEFAULT_AFFINITY_BANDS,
} from './bands';
import { TIER_COLORS } from './AffinityNetwork/affinity.constants';

// Page gold — matches the underline-variant tabs and the rest of Expert IQ.
const GOLD = 'var(--accent)';

interface Props {
  affinityBands: AffinityBands;
}

// A plain answer paragraph. Text is passed as a JS expression so apostrophes in
// the copy don't trip react/no-unescaped-entities.
const Para: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography color="var(--text-2)" sx={{ fontSize: 14, lineHeight: 1.7, mb: 1.5 }}>
    {children}
  </Typography>
);

// Live affinity-tier ladder, highest tier first. Ranges are derived from the
// current affinityBands (falling back to the defaults) so the cited percentages
// stay correct if an admin retunes the bands — mirrors the `alpha` card in
// AlgorithmExplainerCards.
const TierLadder: FC<{ affinityBands: AffinityBands }> = ({ affinityBands }) => (
  <Box className="flex flex-col gap-1.5" sx={{ my: 1 }}>
    {[...AFFINITY_GRADES].reverse().map((grade) => {
      const meta = GRADE_META[grade];
      const entry = affinityBands[grade] ?? DEFAULT_AFFINITY_BANDS[grade];
      const low = Math.round(entry.band * 100);
      const high = Math.round((entry.band + entry.width) * 100);
      return (
        <Box key={grade} className="flex items-center gap-2">
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '3px',
              bgcolor: TIER_COLORS[meta.tier],
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 132 }}>
            {meta.label}
          </Typography>
          <Typography color="var(--text-2)" sx={{ fontSize: 13 }}>
            {low}–{high}%
          </Typography>
        </Box>
      );
    })}
  </Box>
);

// Brand-shareable Q&A — plain language an account manager can read or paste to a
// brand. No equations, no references to internal tuning controls. The answers
// describe the live (banded) algorithm: affinity sorts creators into score
// tiers; attribute fit and experience only reorder creators within a tier.
const buildFaq = (affinityBands: AffinityBands): { q: string; a: ReactNode }[] => [
  {
    q: 'Why are there no high-scoring experts for a product?',
    a: (
      <>
        <Para>
          A creator&apos;s score is driven first and foremost by{' '}
          <b>affinity</b> — how closely their area of focus and professions match
          your product&apos;s category. Affinity sorts creators into tiers that set
          how high they can score:
        </Para>
        <TierLadder affinityBands={affinityBands} />
        <Para>
          If your product sits in a niche or very specific category that few of our
          verified creators specialize in, then no one lands in the top tier — so
          even our most talented, most experienced creators show middle-of-the-road
          scores. It isn&apos;t a knock on those creators; it simply means none of
          them are a strong category match for this particular product. A
          creator&apos;s attributes and years of experience can move them up or down
          within a tier, but only a genuine category match can reach the top scores.
        </Para>
      </>
    ),
  },
  {
    q: 'Why don’t I see a bump for experience between 12 and 17 years, when everything else is the same?',
    a: (
      <>
        <Para>
          Experience is measured on a curve with diminishing returns: the first few
          years in a profession count for a lot, and each additional year after that
          adds less. By around 10–12 years a creator is already counted as
          essentially fully experienced — roughly 91% of full credit at 12 years
          versus about 97% at 17 years — so the gap between the two is only a couple
          of percentage points at most, and often less.
        </Para>
        <Para>
          This is intentional: we don&apos;t want a 30-year veteran to automatically
          overshadow a perfectly qualified creator with a decade of experience when
          everything else about the fit is the same. So two creators who differ only
          in 12 versus 17 years will look essentially tied.
        </Para>
      </>
    ),
  },
  {
    q: 'Why is affinity the most prominent factor?',
    a: (
      <>
        <Para>
          Affinity answers the most important question first: does this creator
          actually work in the kind of thing your product is? We treat that as the
          foundation of the match, not just one ingredient among many.
        </Para>
        <Para>
          Because of that, a creator whose profession doesn&apos;t line up with your
          product&apos;s category immediately drops to a lower tier — typically
          scoring under about 50% — while only creators who genuinely work in your
          product&apos;s category can reach the highest scores. It&apos;s deliberate:
          a brilliant, highly experienced creator in an unrelated field shouldn&apos;t
          out-rank a strong creator who actually specializes in what you&apos;re
          making. In short, affinity decides which tier a creator is in; their
          attributes and experience decide their rank within that tier.
        </Para>
      </>
    ),
  },
  {
    q: 'What’s the difference between “Top Experts” and the “Ideal Profile”?',
    a: (
      <Para>
        <b>Top Experts</b> are the real, verified creators we currently have, ranked
        by how well they match your product. The <b>Ideal Profile</b> describes the
        ideal combination of professions to look for — the perfect-on-paper match —
        even if no single creator with exactly that mix exists yet. Think of Top
        Experts as &ldquo;who we can offer you today,&rdquo; and the Ideal Profile as
        &ldquo;the target we&apos;re matching against.&rdquo;
      </Para>
    ),
  },
  {
    q: 'Can a great creator in the wrong category outrank a good one in the right category?',
    a: (
      <Para>
        No. Because category fit sets the tier, a creator who works in your
        product&apos;s category will always rank above one who doesn&apos;t — even if
        the second creator has stronger attributes or more years of experience. The
        right category always wins; attributes and experience only break ties between
        creators who are already a category match.
      </Para>
    ),
  },
  {
    q: 'Two creators work in the same category with similar experience — why does one score higher?',
    a: (
      <Para>
        When two creators are in the same tier, the tie-breaker is how well the rest
        of their profile fits your product — things like the audience or age group,
        the product tier, and the specific function or use of the product. The
        creator who shares more of these important attributes with your product, and
        whose professions are most relevant to it, scores higher.
      </Para>
    ),
  },
  {
    q: 'A creator who clearly fits shows a very low score (or 0%) — why?',
    a: (
      <Para>
        This usually means the system doesn&apos;t yet have the information it needs
        to recognize the fit — most often the creator&apos;s profession on file
        isn&apos;t mapped to your product&apos;s category, or the product itself is
        categorized in a way that points at a different set of attributes. Once the
        creator&apos;s professions and the product&apos;s category are set correctly,
        the score reflects the true fit. If a creator looks like an obvious match but
        scores oddly low, flag it so we can check the underlying setup.
      </Para>
    ),
  },
  {
    q: 'How fresh are the scores, and why might they change?',
    a: (
      <Para>
        Scores are calculated across every creator and product and refreshed
        periodically. When we refine how the matching works — for example adjusting
        how much weight different attributes carry — scores are recalculated for
        everyone, so you may see a creator&apos;s score shift slightly between visits.
        The ranking logic stays the same; only the precise numbers move as the model
        is tuned.
      </Para>
    ),
  },
  {
    q: 'Why do scores rarely reach 100%?',
    a: (
      <Para>
        A perfect 100% would require a flawless match on everything at once — an exact
        category match, a perfect overlap on every targeted attribute, and maximum
        relevant experience, all together. Real creators almost never line up
        perfectly on all three, so even excellent matches usually land in the 80s or
        low 90s. A score in that range is a very strong match, not a shortfall.
      </Para>
    ),
  },
];

export const ExpertIQFAQ: FC<Props> = ({ affinityBands }) => {
  const faq = buildFaq(affinityBands);
  return (
    <Paper sx={{ p: 3 }}>
      <Typography fontWeight={600}>Frequently asked questions</Typography>
      <Typography color="var(--text-2)" sx={{ fontSize: 14, pb: 2, maxWidth: 760 }}>
        Plain-language answers to the questions we hear most about how Expert IQ
        matches and ranks creators for your products. Written so you can share them
        directly with a brand.
      </Typography>

      {faq.map((item, i) => (
        <Accordion
          key={i}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid var(--border)',
            borderRadius: 1.5,
            mb: 1,
            '&:before': { display: 'none' },
            '&.Mui-expanded': { borderColor: GOLD },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: GOLD }} />}
            sx={{ '& .MuiAccordionSummary-content': { my: 1.25 } }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{item.q}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>{item.a}</AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
};

export default ExpertIQFAQ;
